import axios from 'axios'
import { apiBaseUrl } from '../const'
import { tokenData } from './utils'

const apiClient = axios.create({
  baseURL: import.meta.env.VITE_ADMIN_API_BASE_URL || apiBaseUrl,
  headers: {
    'Content-Type': 'application/json',
    Accept: '*/*',
  },
})

// Ajouter token automatiquement (support impersonation + agent direct login)
apiClient.interceptors.request.use(
  (config) => {
    // Priority: impersonation_token > sessionStorage admin_token > localStorage admin_token
    const token = sessionStorage.getItem('impersonation_token')
      || sessionStorage.getItem('admin_token')
      || localStorage.getItem('admin_token')
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  (error) => {
    return Promise.reject(error)
  },
)

// Manage api
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Don't wipe admin tokens during impersonation or agent sessions
      const isImpersonating = !!sessionStorage.getItem('impersonation_token')
      const isAgentSession = !!sessionStorage.getItem('admin_token')
      if (isImpersonating) {
        sessionStorage.removeItem('impersonation_token')
        sessionStorage.removeItem('impersonation_session')
      } else if (isAgentSession) {
        sessionStorage.removeItem('admin_token')
        sessionStorage.removeItem('role')
      } else {
        localStorage.removeItem('admin_token')
        localStorage.removeItem('role')
      }
      window.location.href = '/auth/login'
    }
    return Promise.reject(error)
  },
)
/**
 * Returns the active token.
 * Priority order:
 * 1. impersonation_token from sessionStorage (impersonation sessions)
 * 2. admin_token from sessionStorage (agent direct login)
 * 3. admin_token from localStorage (admin login)
 */
export const getActiveToken = (): string | null => {
  // 1. Impersonation token (highest priority)
  const impersonationToken = sessionStorage.getItem('impersonation_token')
  if (impersonationToken) {
    return impersonationToken
  }
  
  // 2. Agent token from sessionStorage (agent direct login)
  const sessionToken = sessionStorage.getItem('admin_token')
  if (sessionToken) {
    return sessionToken
  }
  
  // 3. Admin token from localStorage (admin login)
  return localStorage.getItem('admin_token')
}

// add token — uses sessionStorage when impersonating, localStorage otherwise
apiClient.interceptors.request.use(
  (config) => {
    const token = getActiveToken()
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  (error) => {
    return Promise.reject(error)
  },
)

// global error management

apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Déterminer le type d'utilisateur et le storage utilisé
      const isImpersonation = !!sessionStorage.getItem('impersonation_session') || 
                              !!sessionStorage.getItem('impersonation_token')
      const isAgentDirectLogin = !!sessionStorage.getItem('admin_token') && 
                                 sessionStorage.getItem('role') === 'agent' &&
                                 !isImpersonation
      const hasImpersonationTraces = localStorage.getItem('impersonation_was_used')
      
      if (isImpersonation) {
        // CAS 1: AGENT IMPERSONÉ - Nettoyer seulement sessionStorage
        sessionStorage.removeItem('impersonation_session')
        sessionStorage.removeItem('impersonation_token')
        sessionStorage.removeItem('app_local_theme')
        sessionStorage.removeItem('app_locale')
        console.log('🛡️ AXIOS: Impersonation 401 - cleared sessionStorage only, admin protected')
        
        // Redirection vers page de logout agent
        window.location.replace('/auth/agent-logout')
      } else if (isAgentDirectLogin) {
        // CAS 2: AGENT LOGIN DIRECT - Nettoyer sessionStorage et rediriger
        sessionStorage.removeItem('admin_token')
        sessionStorage.removeItem('role')
        sessionStorage.removeItem('app_local_theme')
        sessionStorage.removeItem('app_locale')
        console.log('🔐 AXIOS: Agent direct login 401 - cleared sessionStorage')
        
        window.location.href = '/auth/login'
      } else if (!hasImpersonationTraces) {
        // CAS 3: ADMIN PUR - Nettoyer localStorage seulement s'il n'y a aucune trace d'impersonation
        console.log('🔐 AXIOS: Admin 401 - clearing localStorage')
        localStorage.removeItem('admin_token')
        localStorage.removeItem('role')
        window.location.href = '/auth/login'
      } else {
        // ADMIN avec traces d'impersonation: PROTÉGER admin_token
        console.log('🛡️ AXIOS: Admin 401 with impersonation traces - admin_token protected')
        // Ne pas supprimer admin_token, laisser l'utilisateur sur la page
      }
    }
    return Promise.reject(error)
  },
)

export default apiClient
